import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';
import Header from './components/Header';
import Footer from './components/Footer';
import HomeScreen from './screens/HomeScreen';
import ProductScreen from './screens/ProductScreen';
import LoginScreen from './screens/LoginScreen';
import RegisterScreen from './screens/RegisterScreen';
import ProfileScreen from './screens/ProfileScreen';
import CategoryScreen from './screens/CategoryScreen';
import UploadScreen from './screens/UploadScreen';
import VerifyScreen from './screens/VerifyScreen';
import UsersProductsScreen from './components/Profile/Screens/UsersProductsScreen';
import ShoppingCartScreen from './screens/ShoppingCartScreen';
import OrderScreenContainer from './components/Orders/OrderScreenContainer';
import CompleteOrders from './components/Orders/CompleteOrders';
import OrderScreen from './components/Profile/Screens/OrderScreen';
import OrderDetail from './components/Profile/Order/OrderDetail';
import HelpContact from './screens/HelpContact';
import ShippingInfoScreen from './screens/ShippingInfoScreen';
import SellerInfo from './screens/SellerInfoScreen';
import BuyerInfo from './screens/BuyerInfoScreen';
import TermsScreen from './screens/TermsScreen';
import PrivacyScreen from './screens/PrivacyScreen';
import AddressScreen from './components/Profile/Screens/AddressScreen';
import CreditCardScreen from './components/Profile/Screens/CreditCardScreen';
import BidScreen from './components/Profile/Screens/BidScreen';

// Dynamic Screen Loader Component
const DynamicScreenLoader = ({ path }) => {
    const [ScreenComponent, setScreenComponent] = React.useState(null);
    const [error, setError] = React.useState(null);
    const navigate = useNavigate();

    React.useEffect(() => {
        const loadScreen = async () => {
            try {
                // Clean up the path and convert to screen name
                const cleanPath = path.replace(/^\/+|\/+$/g, ''); // Remove leading/trailing slashes
                const screenName = cleanPath.charAt(0).toUpperCase() + cleanPath.slice(1) + 'Screen';
                                  
                // Try to dynamically import the screen
                const module = await import(`./screens/${screenName}.js`);
 
                setScreenComponent(() => module.default);
            } catch (err) {
                console.error('Error loading screen:', err);
                console.error('Error details:', {
                    message: err.message,
                    stack: err.stack,
                    path: path
                });
                setError(err);
                // Redirect to home page after 3 seconds if screen not found
                setTimeout(() => {
                    navigate('/');
                }, 3000);
            }
        };

        loadScreen();
    }, [path, navigate]);

    if (error) {
        return (
            <Container className="py-5 text-center">
                <h2>Oops! Something went wrong.</h2>
                <p>The requested page could not be found or loaded.</p>
                <p>Error: {error.message}</p>
                <p>Path: {path}</p>
                <p>Redirecting to home page...</p>
            </Container>
        );
    }

    if (!ScreenComponent) {
        return (
            <Container className="py-5 text-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3">Loading page...</p>
            </Container>
        );
    }

    return <ScreenComponent />;
};

// Dynamic Route Handler
const DynamicRouteHandler = () => {
    const location = useLocation();
    const { announcements = [] } = useSelector(state => state.announcementList || {});
    
    // Check if the current path matches any announcement link
    const matchingAnnouncement = announcements?.find(
        announcement => announcement.link === location.pathname
    );

    if (matchingAnnouncement) {
        return <DynamicScreenLoader path={location.pathname} />;
    }

    // If no matching announcement, try to load the screen directly
    return <DynamicScreenLoader path={location.pathname} />;
};

function App() {
    return (
        <Router>
            <Header />
            <main className="py-3">
                <Container>
                    <Routes>
                        <Route path="/" element={<HomeScreen />} exact />
                        <Route path="/product/:productParams" element={<ProductScreen />} />
                        <Route path="/login" element={<LoginScreen />} />
                        <Route path="/register" element={<RegisterScreen />} />
                        <Route path="/verify/:uidb64/:token" element={<VerifyScreen />} />
                        <Route path="/profile" element={<ProfileScreen />}>
                            <Route index element={<BidScreen />} />
                            <Route path="selling" element={<UsersProductsScreen />} />
                            <Route path="addresses" element={<AddressScreen />} />
                            <Route path="cards" element={<CreditCardScreen />} />
                            <Route path="orders/buying" element={<OrderScreen />} />
                            <Route path="orders/buying/:orderID" element={<OrderDetail />} />
                            
                        </Route>
                        <Route path="/shopping-cart" element={<ShoppingCartScreen />}>
                            <Route index element={<OrderScreenContainer />} />
                            <Route path="complete" element={<CompleteOrders />} />
                        </Route>
                        <Route path="/product/upload" element={<UploadScreen />} />
                        <Route path="/categories/:categoryParams" element={<CategoryScreen />} />
                        <Route path="/?search=:keywordParams" element={<CategoryScreen />} />
                        <Route path="/cs" element={<HelpContact />} />
                        <Route path="/shipping" element={<ShippingInfoScreen />} />
                        <Route path="/seller" element={<SellerInfo />} />
                        <Route path="/buyer" element={<BuyerInfo />} />
                        <Route path="/terms" element={<TermsScreen />} />
                        <Route path="/privacy" element={<PrivacyScreen />} />
                     
                        {/* Catch-all route for dynamic screens */}
                        <Route path="*" element={<DynamicRouteHandler />} />
                    </Routes>
                </Container>
            </main>
            <Footer />
        </Router>
    );
}

export default App;